import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HighlightCard from "../../ui/components/HighlightCard";
import PillButton from "../../ui/components/PillButton";
import {
  getContainerCharacterization,
  getContainerClientRegisteredInfo,
  getListRotationPeriods,
  getCharacterizationContainersList,
  getSuperContainerList,
} from "../../services/container";
import { getSettingsData } from "../../services/settings";
import { IconCharacterization } from "../../components/navigation/navIcons";
import HorizontalBarChart from "../../components/charts/HorizontalBarChart";
import { MaterialCountingPieChart } from "../../components/charts/MaterialCountingPieChart";
import { TreemapChart } from "../../components/charts/TreemapChart";
import { formattedColorMaterial } from "../../components/formatted/FormattedText";
import { InlineLoader } from "../../components/loader/CharacterizationLoader";
import { CharacterizationLoadingStatus } from "./CharacterizationLoadingStatus";
import { ReactComponent as WeightIcon } from "../../static/assets/Weight.svg";
import { formatCharacterizationDate } from "../characterization-2/core/utils/utils";
import { REGULATION_TYPES } from "../characterization-2/characterizationDetails/constants/regulation";
import { useIsAdminViewMode } from "../../hooks/useViewMode";
import {
  buildCharacterizationMassChartData,
  buildCharacterizationObjectChartData,
  buildCharacterizationReportQueryTargets,
  buildWeightedMassComposition,
  buildWeightedObjectComposition,
  dedupeCharacterizationDashboardReports,
  formatCharacterizationRegisteredWeight,
  formatCount,
  getCharacterizationClientLabel,
  getCharacterizationClientQueryBounds,
  getCharacterizationFlowName,
  groupMinorCharacterizationShares,
  mapTargetPeriodsToCharacterizationReports,
  mapWithConcurrency,
  prepareCharacterizationReportMaterials,
  readBooleanStorage,
  resolveNumberLocale,
  writeBooleanStorage,
} from "./dashboardKpiUtils";

const SITE_PERIOD_FETCH_CONCURRENCY = 4;
const REPORT_FETCH_CONCURRENCY = 3;
const FINES_COLOR = "#FFA500";
const OTHERS_COLOR = "#9E9E9E";
const DEFAULT_FINES_THRESHOLD_CM = 40;
const STORAGE_KEY = "dashboard.characterizationsOpen";

const getMaterialMergeConfig = () => {
  try {
    const list = JSON.parse(
      localStorage.getItem("materialsDescription") || "[]",
    );
    return (list || []).map((material) => ({
      referenceName: material.referenceName,
      possibleNames: material.possibleNames,
    }));
  } catch {
    return [];
  }
};

const getMaterialColor = (cls) => {
  try {
    return formattedColorMaterial(cls);
  } catch {
    return "rgba(75,192,192,0.6)";
  }
};

const getCompositionColors = (composition, finesLabel) =>
  (composition.keys?.length ? composition.keys : composition.labels || []).map(
    (key, index) => {
      const label = composition.labels?.[index];
      if (key === "others") return OTHERS_COLOR;
      if (key === "fines" || label === finesLabel) return FINES_COLOR;
      return getMaterialColor(key);
    },
  );

const relabelFinesShare = (composition, finesLabel) => ({
  ...composition,
  labels: (composition.labels || []).map((label, index) =>
    composition.keys?.[index] === "fines" ? finesLabel : label,
  ),
});

const formatOthersShareLabel = (t, preview, hasMore) =>
  hasMore
    ? t("dashboard.characterizationOthersMore", { items: preview.join(", ") })
    : t("dashboard.characterizationOthers", { items: preview.join(", ") });

const DashboardCharacterizationReportRow = ({ report, t, locale }) => {
  const dateLabel = formatCharacterizationDate(report.start);
  const flowName = getCharacterizationFlowName(report.waste_category);
  const clientLabel = getCharacterizationClientLabel(report.client);
  const subtitle = [flowName, clientLabel].filter(Boolean).join(" · ");

  const massChart = report.massChart;
  const objectChart = report.objectChart;
  const massHeight = Math.max(220, (massChart?.labels?.length || 0) * 36);
  const objectColors = [
    ...(report.materialDelivery || []).map((material) =>
      getMaterialColor(material.cls),
    ),
    ...(objectChart?.labels?.length > (report.materialDelivery || []).length
      ? [FINES_COLOR]
      : []),
  ];
  const registeredWeight = formatCharacterizationRegisteredWeight(
    report.weight,
    t("tons"),
    locale,
  );

  return (
    <article className="dashboard-chart-card dashboard-char-report-card">
      <div className="dashboard-chart-heading">
        <h4 className="dashboard-chart-title">{dateLabel || "—"}</h4>
        {subtitle ? (
          <p className="dashboard-chart-subtitle">{subtitle}</p>
        ) : null}
      </div>
      {report.status === "loading" ? (
        <InlineLoader height="220px" />
      ) : report.status === "error" ? (
        <p className="dashboard-chart-empty">
          {t("dashboard.characterizationReportUnavailable")}
        </p>
      ) : (
        <>
          <div className="dashboard-char-report-mass-row">
            <div className="dashboard-char-report-mass-chart">
              <h5 className="dashboard-char-report-chart-title">
                {t("Mass analysis")}
              </h5>
              {massChart?.values?.length > 0 ? (
                <div
                  className="dashboard-char-report-bar-chart"
                  style={{ height: `${massHeight}px` }}
                >
                  <HorizontalBarChart
                    dataChart={massChart.values}
                    labelsChart={massChart.labels}
                    unitValueChart="%"
                    colorChart={massChart.colors}
                  />
                </div>
              ) : (
                <p className="dashboard-chart-empty">
                  {t("dashboard.characterizationMassEmpty")}
                </p>
              )}
            </div>
            <HighlightCard
              icon={<WeightIcon />}
              title={t("Registered weight")}
              hint={t("dashboard.characterizationRegisteredWeightHint")}
              value={registeredWeight}
            />
          </div>
          <h5 className="dashboard-char-report-chart-title">
            {t("Objects distribution")}
          </h5>
          {objectChart?.values?.length > 0 && report.materialTotal > 0 ? (
            <div className="dashboard-char-report-pie">
              <MaterialCountingPieChart
                dataChart={objectChart.values}
                labelChart={objectChart.labels}
                materialTotal={report.materialTotal}
                colorChart={objectColors}
                showMaterialTotal={true}
                radius={["30%", "60%"]}
              />
            </div>
          ) : (
            <p className="dashboard-chart-empty">
              {t("dashboard.characterizationObjectsEmpty")}
            </p>
          )}
        </>
      )}
    </article>
  );
};

export const DashboardCharacterizationReports = ({
  periodStart,
  periodStop,
  periodLabel,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isAdmin = useIsAdminViewMode();
  const numberLocale = resolveNumberLocale(i18n.language);
  const [count, setCount] = useState(null);
  const [reports, setReports] = useState([]);
  const [finesThresholdCm, setFinesThresholdCm] = useState(
    DEFAULT_FINES_THRESHOLD_CM,
  );
  const [isOpen, setIsOpen] = useState(() =>
    readBooleanStorage(STORAGE_KEY, true),
  );
  const [showReportDetails, setShowReportDetails] = useState(false);

  const toggleOpen = () => {
    setIsOpen((current) => {
      const next = !current;
      writeBooleanStorage(STORAGE_KEY, next);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    getSettingsData()
      .then((response) => {
        if (cancelled) return;
        const threshold = response.data?.settings?.finesSizeThreshold;
        if (threshold !== undefined && threshold !== null && threshold !== "") {
          setFinesThresholdCm(Number(threshold));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!periodStart || !periodStop) return undefined;
    let cancelled = false;

    const load = async () => {
      setCount(null);
      setReports([]);

      try {
        // Same discovery path as WQ / "en cours": walk every site zone.
        const [containersResponse, superResponse] = await Promise.all([
          getCharacterizationContainersList({ characterizationOnly: false }),
          getSuperContainerList(),
        ]);
        if (cancelled) return;

        const allContainers = containersResponse?.data?.data ?? [];
        const superContainers = superResponse?.data?.superContainers ?? [];
        const targets = buildCharacterizationReportQueryTargets(
          allContainers,
          superContainers,
        );

        if (targets.length === 0) {
          setCount(0);
          setReports([]);
          return;
        }

        const periodResults = await mapWithConcurrency(
          targets,
          SITE_PERIOD_FETCH_CONCURRENCY,
          async (target) => {
            if (cancelled) return [];
            try {
              const params = {
                periodMaterialStart: periodStart,
                periodMaterialStop: periodStop,
                isCharacterization: true,
              };
              if (target.superContainerId) {
                params.superContainerId = target.superContainerId;
              }
              const response = await getListRotationPeriods(
                params,
                target.containerId,
              );
              return mapTargetPeriodsToCharacterizationReports(
                response?.data?.periods,
                target,
              );
            } catch {
              // One zone failing must not zero the whole dashboard KPI.
              return [];
            }
          },
        );
        if (cancelled) return;

        const listed = dedupeCharacterizationDashboardReports(
          periodResults.flat(),
        );
        const mergeConfig = getMaterialMergeConfig();
        const finesLabel = t("Fines");

        setReports(listed.map((report) => ({ ...report, status: "loading" })));
        setCount(listed.length);

        await mapWithConcurrency(
          listed,
          REPORT_FETCH_CONCURRENCY,
          async (report) => {
            if (cancelled) return report;
            const zoneId = report.zoneId || report.containerId;
            try {
              const bounds = getCharacterizationClientQueryBounds(
                report.start,
                report.stop,
              );
              const [response, clientResponse] = await Promise.all([
                getContainerCharacterization(
                  {
                    periodMaterialStart: report.start,
                    periodMaterialStop: report.stop,
                    customDate: true,
                    language: i18n.language,
                    characterizationType: REGULATION_TYPES.CLASSIC,
                  },
                  zoneId,
                ),
                bounds
                  ? getContainerClientRegisteredInfo(bounds, zoneId).catch(
                      () => null,
                    )
                  : Promise.resolve(null),
              ]);
              if (cancelled) return report;
              const data = response?.data || {};
              const clientData = clientResponse?.data || {};
              const materialDelivery = prepareCharacterizationReportMaterials(
                data.materialDelivery,
                mergeConfig,
              );
              const massChart = buildCharacterizationMassChartData({
                materialDelivery,
                totalWasteMass: data.totalWasteMass || 0,
                totalFinesMass: data.totalFinesMass || 0,
                finesBelowThreshold: data.finesBelowThreshold,
                finesLabel,
              });
              const objectChart = buildCharacterizationObjectChartData({
                materialDelivery,
                finesBelowThreshold: data.finesBelowThreshold,
                totalFinesVolume: data.totalFinesVolume || 0,
                finesLabel,
                translateLabel: (cls) => t(cls),
              });
              const readyReport = {
                ...report,
                status: "ready",
                materialDelivery,
                materialTotal: data.materialTotal || 0,
                massChart,
                objectChart,
                weight:
                  clientData.weight != null && clientData.weight !== ""
                    ? clientData.weight
                    : report.weight,
                client: clientData.client || report.client,
                waste_category:
                  clientData.waste_category || report.waste_category,
              };
              setReports((current) =>
                current.map((item) =>
                  item.key === report.key ? readyReport : item,
                ),
              );
              return readyReport;
            } catch {
              if (cancelled) return report;
              const failedReport = { ...report, status: "error" };
              setReports((current) =>
                current.map((item) =>
                  item.key === report.key ? failedReport : item,
                ),
              );
              return failedReport;
            }
          },
        );
      } catch {
        if (!cancelled) {
          setCount(0);
          setReports([]);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [periodStart, periodStop, i18n.language, t]);

  const reportsLoading = reports.some((report) => report.status === "loading");
  const loadedCount = reports.filter(
    (report) => report.status !== "loading",
  ).length;
  const showLoadingStatus =
    count == null || reportsLoading || (count > 0 && loadedCount < count);
  const finesDisplayLabel =
    Number(finesThresholdCm) > 0
      ? t("Fines (below {{threshold}} cm)", { threshold: finesThresholdCm })
      : t("Fines");
  const weightedMass = useMemo(() => {
    if (reportsLoading) {
      return {
        labels: [],
        values: [],
        keys: [],
        colors: [],
        totalWeight: null,
      };
    }
    const formatOthersLabel = (preview, hasMore) =>
      formatOthersShareLabel(t, preview, hasMore);
    return relabelFinesShare(
      groupMinorCharacterizationShares(buildWeightedMassComposition(reports), {
        formatOthersLabel,
      }),
      finesDisplayLabel,
    );
  }, [finesDisplayLabel, reports, reportsLoading, t]);
  const weightedObjects = useMemo(() => {
    if (reportsLoading) {
      return {
        labels: [],
        values: [],
        keys: [],
        colors: [],
        totalObjects: null,
      };
    }
    const formatOthersLabel = (preview, hasMore) =>
      formatOthersShareLabel(t, preview, hasMore);
    return relabelFinesShare(
      groupMinorCharacterizationShares(
        buildWeightedObjectComposition(reports),
        { formatOthersLabel },
      ),
      finesDisplayLabel,
    );
  }, [finesDisplayLabel, reports, reportsLoading, t]);
  const objectTotalColors = useMemo(
    () => getCompositionColors(weightedObjects, finesDisplayLabel),
    [finesDisplayLabel, weightedObjects],
  );
  const massTotalColors = useMemo(
    () => getCompositionColors(weightedMass, finesDisplayLabel),
    [finesDisplayLabel, weightedMass],
  );
  const totalMassHeight = Math.max(
    220,
    (weightedMass.labels?.length || 0) * 36,
  );

  return (
    <section className="dashboard-sorting-errors">
      <div className={`dashboard-sorting-header${isOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="dashboard-sorting-toggle"
          onClick={toggleOpen}
          aria-expanded={isOpen}
          aria-controls="dashboard-characterizations-panel"
        >
          <h3 className="dashboard-campaigns-title">
            {t("Characterizations")}
          </h3>
          <i
            className={`fas fa-chevron-down dashboard-sorting-chevron${isOpen ? " is-open" : ""}`}
            aria-hidden="true"
          />
        </button>
        <PillButton
          size="md"
          onClick={() => history.push("/characterization-areas")}
        >
          {t("dashboard.viewCharacterizationsDetails")}
        </PillButton>
      </div>
      {isOpen ? (
        <div
          id="dashboard-characterizations-panel"
          className="dashboard-kpi-panel"
        >
          <div className="dashboard-volume-row">
            <HighlightCard
              icon={<IconCharacterization />}
              title={t("dashboard.characterizationReports")}
              hint={t("dashboard.characterizationReportsHint")}
              value={formatCount(count, numberLocale)}
            />
            <HighlightCard
              icon={<WeightIcon />}
              title={t("Registered weight")}
              hint={t("dashboard.characterizationRegisteredWeightTotalHint")}
              value={formatCharacterizationRegisteredWeight(
                weightedMass.totalWeight,
                t("tons"),
                numberLocale,
              )}
            />
            <HighlightCard
              icon={<IconCharacterization />}
              title={t("dashboard.characterizationObjectsTotal")}
              hint={t("dashboard.characterizationObjectsTotalHint")}
              value={formatCount(weightedObjects.totalObjects, numberLocale)}
            />
          </div>
          {showLoadingStatus ? (
            <CharacterizationLoadingStatus
              count={count}
              loaded={loadedCount}
              periodLabel={periodLabel}
            />
          ) : null}
          {!showLoadingStatus && weightedObjects.values.length > 0 ? (
            <article className="dashboard-chart-card">
              <div className="dashboard-chart-heading">
                <h4 className="dashboard-chart-title">
                  {t("dashboard.characterizationObjectTotal")}
                </h4>
              </div>
              <div className="dashboard-char-report-treemap">
                <TreemapChart
                  dataChart={weightedObjects.values}
                  labelsChart={weightedObjects.labels}
                  colorChart={objectTotalColors}
                  locale={numberLocale}
                />
              </div>
            </article>
          ) : null}
          {!showLoadingStatus && weightedMass.values.length > 0 ? (
            <article className="dashboard-chart-card">
              <div className="dashboard-chart-heading dashboard-chart-heading-row">
                <div className="dashboard-chart-heading-text">
                  <h4 className="dashboard-chart-title">
                    {t("dashboard.characterizationMassTotal")}
                  </h4>
                </div>
                {isAdmin ? (
                  <PillButton
                    size="sm"
                    pressed={showReportDetails}
                    onClick={() => setShowReportDetails((current) => !current)}
                  >
                    {t("dashboard.viewCharacterizationReportsDetails")}
                  </PillButton>
                ) : null}
              </div>
              <div
                className="dashboard-char-report-bar-chart"
                style={{ height: `${totalMassHeight}px` }}
              >
                <HorizontalBarChart
                  dataChart={weightedMass.values}
                  labelsChart={weightedMass.labels}
                  unitValueChart="%"
                  colorChart={massTotalColors}
                  showTooltip={false}
                />
              </div>
            </article>
          ) : null}
          {isAdmin && showReportDetails && reports.length > 0 ? (
            <div className="dashboard-char-report-list">
              {reports.map((report) => (
                <DashboardCharacterizationReportRow
                  key={report.key}
                  report={report}
                  t={t}
                  locale={numberLocale}
                />
              ))}
            </div>
          ) : count === 0 ? (
            <p className="dashboard-chart-empty">
              {t("dashboard.characterizationReportsEmpty")}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
